import React, { useState } from 'react';
import axios from 'axios';
import { Gift, X } from 'lucide-react';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Textarea } from './ui/textarea';
import { toast } from 'sonner';
import { API, track } from '../App';

/**
 * "Drop a Hint" modal — a shopper sends a gift hint email to a friend/spouse.
 * Triggered from a button on the product detail page (great for gift-worthy
 * items like the Christ Kingdom Apparel POD line).
 */
export default function DropHintModal({ product, open, onClose }) {
  const [senderName, setSenderName] = useState('');
  const [senderEmail, setSenderEmail] = useState('');
  const [recipientEmail, setRecipientEmail] = useState('');
  const [message, setMessage] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [done, setDone] = useState(false);

  if (!open || !product) return null;

  const submit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    try {
      await axios.post(`${API}/hints/send`, {
        product_id: product.product_id,
        sender_name: senderName,
        sender_email: senderEmail,
        recipient_email: recipientEmail,
        message,
      });
      setDone(true);
      track('Lead', {
        content_name: 'drop_a_hint',
        content_ids: [product.product_id],
        value: product.price,
      });
      toast.success('Gift hint sent! 🎁');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Could not send the hint');
    } finally {
      setSubmitting(false);
    }
  };

  const close = () => {
    setDone(false);
    setSenderName('');
    setSenderEmail('');
    setRecipientEmail('');
    setMessage('');
    onClose();
  };

  return (
    <div
      className="fixed inset-0 z-[100] flex items-center justify-center bg-foreground/50 backdrop-blur-sm p-4"
      data-testid="drop-hint-modal"
      onClick={close}
    >
      <div
        className="relative bg-background rounded-2xl shadow-2xl max-w-md w-full overflow-hidden"
        onClick={(e) => e.stopPropagation()}
      >
        <button
          onClick={close}
          aria-label="Close"
          className="absolute top-3 right-3 p-1 text-muted-foreground hover:text-foreground z-10"
          data-testid="drop-hint-close"
        >
          <X className="h-5 w-5" />
        </button>

        <div className="bg-gradient-to-br from-primary to-secondary text-white p-6 text-center">
          <Gift className="h-10 w-10 mx-auto mb-2" />
          <h2 className="text-2xl font-medium font-heading">Drop a Hint</h2>
          <p className="text-white/90 text-sm mt-1">Let someone know what you&apos;d love</p>
        </div>

        <div className="p-6">
          {done ? (
            <div className="text-center space-y-3">
              <p className="text-lg font-medium">Hint sent 💌</p>
              <p className="text-sm text-muted-foreground">
                We emailed them a beautiful note with the product details.
              </p>
              <Button onClick={close} className="w-full" data-testid="drop-hint-done">
                Done
              </Button>
            </div>
          ) : (
            <form onSubmit={submit} className="space-y-3">
              <p className="text-sm text-muted-foreground">
                We&apos;ll send <strong>{product.name}</strong> as a beautifully formatted gift hint.
              </p>
              <Input
                placeholder="Your name"
                value={senderName}
                onChange={(e) => setSenderName(e.target.value)}
                required
                data-testid="drop-hint-sender-name"
              />
              <Input
                type="email"
                placeholder="Your email (so they know who sent it)"
                value={senderEmail}
                onChange={(e) => setSenderEmail(e.target.value)}
                required
                data-testid="drop-hint-sender-email"
              />
              <Input
                type="email"
                placeholder="Their email"
                value={recipientEmail}
                onChange={(e) => setRecipientEmail(e.target.value)}
                required
                data-testid="drop-hint-recipient-email"
              />
              <Textarea
                placeholder="Add a personal note (optional)…"
                value={message}
                onChange={(e) => setMessage(e.target.value)}
                rows={3}
                data-testid="drop-hint-message"
              />
              <Button
                type="submit"
                className="w-full"
                disabled={submitting}
                data-testid="drop-hint-submit"
              >
                {submitting ? 'Sending…' : 'Send the Hint 🎁'}
              </Button>
            </form>
          )}
        </div>
      </div>
    </div>
  );
}
